class RedeleaseFreeShipping extends HTMLElement {
  static observedAttributes = ['data-cart-total'];

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.message = this.querySelector('[data-free-shipping-message]');
    this.track = this.querySelector('[data-free-shipping-track]');
    this.fill = this.querySelector('[data-free-shipping-fill]');
    this.form = this.querySelector('[data-free-shipping-form]');
    this.input = this.querySelector('[data-free-shipping-input]');
    this.error = this.querySelector('[data-free-shipping-error]');
    this.postalStatus = this.querySelector('[data-free-shipping-postal-status]');
    this.postalCodeLabel = this.querySelector('[data-free-shipping-postal-code]');
    this.changeButton = this.querySelector('[data-free-shipping-change]');
    this.cartDrawer = this.closest('cart-drawer-component');
    this.config = this.readConfig();
    this.rules = this.parseRules(this.config.postalRules);

    const { signal } = this.abortController;
    this.form?.addEventListener('submit', this.handleSubmit, { signal });
    this.input?.addEventListener('input', this.handleInput, { signal });
    this.changeButton?.addEventListener('click', this.handleChange, { signal });
    document.addEventListener('redelease:postal-code', this.handlePostalCodeChange, { signal });
    document.addEventListener('redelease:postal-code-validated', this.handleValidatedPostalCode, { signal });
    window.addEventListener('storage', this.handleStorage, { signal });
    this.cartDrawer?.addEventListener('dialog:open', this.handleDrawerOpen, { signal });

    this.update();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-cart-total' || oldValue === null || oldValue === newValue || !this.isConnected) return;

    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.config = this.readConfig();
      this.rules = this.parseRules(this.config.postalRules);
      this.update();
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    const postalCode = this.normalizePostalCode(this.input?.value);

    if (postalCode.length !== 8) {
      this.showError('Informe um CEP válido com 8 dígitos.');
      return;
    }

    this.clearError();
    this.savePostalCode(postalCode);
    const isValidPostalCode = await this.validatePostalCode(postalCode);
    if (!isValidPostalCode) {
      this.showError('CEP não encontrado. Confira os números informados.');
      return;
    }

    this.markPostalCodeValidated(postalCode);
    this.update(postalCode);
  };

  handleInput = () => {
    if (!this.input) return;
    this.input.value = this.formatPostalCode(this.input.value);
    this.clearError();
  };

  handleChange = () => {
    if (!this.form || !this.input) return;
    this.form.hidden = false;
    this.input.value = this.formatPostalCode(this.getPostalCode());
    this.input.focus();
    this.input.select();
  };

  handlePostalCodeChange = (event) => {
    this.update(event.detail?.postalCode);
  };

  handleStorage = (event) => {
    if (event.key === 'redelease_postal_code' || event.key === 'redelease_validated_postal_code') this.update();
  };

  handleValidatedPostalCode = (event) => {
    this.update(event.detail?.postalCode);
  };

  handleDrawerOpen = () => {
    const postalCode = this.getPostalCode();
    this.update(postalCode);

    if (this.input && !this.form?.hidden) {
      this.input.value = this.formatPostalCode(postalCode);
    }
  };

  update(postalCodeValue) {
    const postalCode = this.normalizePostalCode(postalCodeValue || this.getPostalCode());
    const validatedPostalCode = this.getValidatedPostalCode();
    const rule = this.findRule(postalCode);
    const usesCartCalculator = !this.form;

    this.classList.remove('redelease-free-shipping--complete');
    this.clearError();

    if (postalCode.length !== 8 || validatedPostalCode !== postalCode) {
      this.hidden = usesCartCalculator;
      this.setMessage('');
      this.setProgress(null);
      this.setPostalStatus(null);
      this.showForm('');
      return;
    }

    this.hidden = false;

    if (!rule) {
      this.hidden = true;
      this.setProgress(null);
      this.setPostalStatus(null);
      return;
    }

    const cartTotal = Number(this.dataset.cartTotal || 0);
    const goalInCents = Math.round(rule.goal * 100);
    const remaining = Math.max(goalInCents - cartTotal, 0);
    const progress = goalInCents > 0 ? Math.min((cartTotal / goalInCents) * 100, 100) : 100;
    const complete = remaining === 0;
    const message = complete
      ? this.config.successMessage || 'Parabéns! Você ganhou frete grátis.'
      : (this.config.progressMessage || 'Faltam [valor] para você ganhar frete grátis!').replaceAll(
          '[valor]',
          this.formatMoney(remaining)
        );

    this.classList.toggle('redelease-free-shipping--complete', complete);
    this.setMessage(message);
    this.setProgress(progress, message);
    this.setPostalStatus(postalCode);
    if (this.form) this.form.hidden = true;
  }

  readConfig() {
    const element = this.querySelector('[data-free-shipping-config]');
    if (!element) return {};

    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      console.error('Redelease free shipping configuration is invalid', error);
      return {};
    }
  }

  parseRules(value) {
    return (value || '')
      .split(/\r?\n/)
      .map((line) => {
        const postalCodes = line.match(/\d{5}-?\d{3}/g);
        if (!postalCodes || postalCodes.length < 2) return null;

        const separatorIndex = Math.max(line.lastIndexOf('|'), line.lastIndexOf(';'));
        const configuredGoal = separatorIndex >= 0 ? this.parseAmount(line.slice(separatorIndex + 1)) : NaN;
        const defaultGoal = Number(this.config.defaultGoal || 300);
        const goal = Number.isFinite(configuredGoal) && configuredGoal > 0 ? configuredGoal : defaultGoal;

        return {
          start: Number(this.normalizePostalCode(postalCodes[0])),
          end: Number(this.normalizePostalCode(postalCodes[1])),
          goal,
        };
      })
      .filter((rule) => rule && Number.isFinite(rule.start) && Number.isFinite(rule.end) && rule.start <= rule.end);
  }

  parseAmount(value) {
    let amount = (value || '').replace(/[^\d,.-]/g, '');
    if (!amount) return NaN;

    if (amount.includes(',')) {
      amount = amount.replaceAll('.', '').replace(',', '.');
    }

    return Number(amount);
  }

  findRule(postalCode) {
    if (postalCode.length !== 8) return null;
    const numericPostalCode = Number(postalCode);
    return this.rules.find((rule) => numericPostalCode >= rule.start && numericPostalCode <= rule.end) || null;
  }

  getPostalCode() {
    if (window.RedeleaseDelivery?.getPostalCode) return window.RedeleaseDelivery.getPostalCode();

    try {
      return window.localStorage.getItem('redelease_postal_code') || '';
    } catch (error) {
      return '';
    }
  }

  getValidatedPostalCode() {
    if (window.RedeleaseDelivery?.getValidatedPostalCode) {
      return window.RedeleaseDelivery.getValidatedPostalCode();
    }

    try {
      return this.normalizePostalCode(window.localStorage.getItem('redelease_validated_postal_code'));
    } catch (error) {
      return '';
    }
  }

  async validatePostalCode(postalCode) {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return false;
      const payload = await response.json();
      return !payload?.erro && /^[A-Z]{2}$/.test(payload?.uf?.toString().toUpperCase() || '');
    } catch (error) {
      return false;
    }
  }

  markPostalCodeValidated(postalCode) {
    if (window.RedeleaseDelivery?.markPostalCodeValidated) {
      window.RedeleaseDelivery.markPostalCodeValidated(postalCode);
      return;
    }

    try {
      window.localStorage.setItem('redelease_validated_postal_code', postalCode);
      document.dispatchEvent(new CustomEvent('redelease:postal-code-validated', { detail: { postalCode } }));
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  savePostalCode(postalCode) {
    if (window.RedeleaseDelivery?.savePostalCode) {
      window.RedeleaseDelivery.savePostalCode(postalCode);
      return;
    }

    try {
      window.localStorage.setItem('redelease_postal_code', postalCode);
      document.dispatchEvent(new CustomEvent('redelease:postal-code', { detail: { postalCode } }));
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  normalizePostalCode(value) {
    if (window.RedeleaseDelivery?.normalizePostalCode) return window.RedeleaseDelivery.normalizePostalCode(value);
    return (value || '').toString().replace(/\D/g, '').slice(0, 8);
  }

  formatPostalCode(value) {
    if (window.RedeleaseDelivery?.formatPostalCode) return window.RedeleaseDelivery.formatPostalCode(value);
    const digits = this.normalizePostalCode(value);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.config.locale || 'pt-BR', {
        style: 'currency',
        currency: this.config.currency || 'BRL',
      }).format(cents / 100);
    } catch (error) {
      return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  setMessage(message) {
    if (this.message) this.message.textContent = message;
  }

  setProgress(progress, message = '') {
    if (!this.track || !this.fill) return;
    const hidden = progress === null;
    this.track.hidden = hidden;
    if (hidden) {
      this.fill.style.width = '0%';
      this.track.setAttribute('aria-valuenow', '0');
      this.track.removeAttribute('aria-label');
      return;
    }

    const roundedProgress = Math.round(progress);
    this.fill.style.width = `${progress}%`;
    this.track.setAttribute('aria-valuenow', roundedProgress.toString());
    this.track.setAttribute('aria-label', message);
  }

  setPostalStatus(postalCode) {
    if (!this.postalStatus || !this.postalCodeLabel) return;
    this.postalStatus.hidden = !postalCode;
    this.postalCodeLabel.textContent = postalCode ? `CEP ${this.formatPostalCode(postalCode)}` : '';
  }

  showForm(postalCode) {
    if (!this.form || !this.input) return;
    this.form.hidden = false;
    this.input.value = this.formatPostalCode(postalCode);
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  clearError() {
    if (!this.error) return;
    this.error.textContent = '';
    this.error.hidden = true;
  }
}

if (!customElements.get('redelease-free-shipping')) {
  customElements.define('redelease-free-shipping', RedeleaseFreeShipping);
}
